import { useState } from 'react'
import { Popover } from '@base-ui/react/popover'
import { Download, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import {
  EXPORT_SCOPE_OPTIONS,
  type ExportScope,
} from '@/admin/lib/export-scope'

interface ExportMenuProps {
  /** Called with the chosen scope when the user picks a download option. */
  onExport: (scope: ExportScope) => void
  isExporting?: boolean
  /** "compact" = icon-only trigger (registrations/claims); "classic" = icon + label. */
  layout?: 'compact' | 'classic'
  /** Appended to the trigger button so each layout keeps its existing styling. */
  triggerClassName?: string
}

/**
 * Download button that opens a scope picker (Current view / period presets /
 * All time) instead of firing a single fixed export. Mirrors the visual
 * language of {@link CardActionMenu}: a base-ui Popover with icon-on-left,
 * label rows, hairline border and soft shadow.
 */
export function ExportMenu({
  onExport,
  isExporting = false,
  layout = 'compact',
  triggerClassName,
}: ExportMenuProps) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const title = t('exportMenu.title', 'Download spreadsheet')

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={title}
        title={title}
        data-testid="export-menu-trigger"
        disabled={isExporting}
        className={cn(
          'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-60',
          triggerClassName,
        )}
      >
        {isExporting ? (
          <Loader2 className={cn('animate-spin', layout === 'compact' ? 'h-3.5 w-3.5' : 'h-3 w-3')} />
        ) : (
          <Download className={layout === 'compact' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
        )}
        {layout === 'classic' && <span>{t('exportMenu.trigger', 'Export')}</span>}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Backdrop className="fixed inset-0 z-40" />
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="isolate z-50">
          <Popover.Popup data-testid="export-scope-menu" className="min-w-[240px] origin-(--transform-origin) overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] p-1 shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95">
            <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {title}
            </div>
            {EXPORT_SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onExport(opt.value)
                  setOpen(false)
                }}
                className="flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-[6px] px-2.5 py-2 text-left outline-none transition-colors hover:bg-[var(--bg-subtle)] focus-visible:bg-[var(--bg-subtle)]"
              >
                <span className="text-[12.5px] font-normal text-[var(--ink-1)]">
                  {t(`exportMenu.scopes.${opt.value}.label`, opt.label)}
                </span>
                <span className="text-[11px] font-normal text-[var(--ink-3)]">
                  {t(`exportMenu.scopes.${opt.value}.hint`, opt.hint)}
                </span>
              </button>
            ))}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
